import React from 'react';
import styles from './ResumeTemplateElegant.module.css';

// Helper function to render list items if data exists
const renderListItems = (items) => {
  if (!items || items.length === 0) {
    return null;
  }
  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
};

const ResumeTemplateElegant = ({ resumeData }) => {
  // Fallback to default data if resumeData is not provided or incomplete
  const data = resumeData || {
    personalInfo: {
      firstName: 'Loraine',
      lastName: 'Hudson',
      jobTitle: 'Bank Teller',
      profilePicture: 'https://via.placeholder.com/150', // Replace with actual image path or prop
      email: 'loraine@novoresume.com',
      phone: '123 445 222',
      address: 'Philadelphia, PA',
      linkedin: 'linkedin.com/in/loraine.hudson',
      skype: 'loraine.hudson', // Assuming 'S' icon is Skype
      summary: 'Analytical, detail-oriented professional with 8+ years of extensive experience in providing exceptional customer service, administering customer transactions, and promoting company offerings in the banking industry. Exceptionally organized and focused, demonstrating an outstanding aptitude to manage and balance a variety of financial transactions in a fast-paced environment.'
    },
    skills: [
      'Cash Drawer Balancing',
      'Cash Handling Skills',
      'Complex Problem-Solving',
      'Staff Training & Development',
      'Exceptional Customer Service',
      'Analytical & Critical Thinking'
    ],
    languages: [
      { language: 'English', proficiency: 'Native or Bilingual Proficiency' },
      { language: 'Spanish', proficiency: 'Full Professional Proficiency' },
      { language: 'French', proficiency: 'Professional Working Proficiency' }
    ],
    certificates: [
      { name: 'Certified Bank Teller (06/2013)', issuingOrganization: 'Bank of Pennsylvania Training Center', link: '#' },
      { name: 'Teller Specialist Certificate Program (06/2013)', issuingOrganization: 'Independent Community Bankers of America (ICBA)', link: '#' }
    ],
    awards: [
      { name: 'Bank Teller of the Year (2019)', institution: 'Double Star National Bank' },
      { name: 'The Employee of the Month - 8 times in 4 years', institution: 'Bank of Pennsylvania, Lancaster Branch' }
    ],
    interests: ['Mediation', 'Archery', 'Gardening', 'Photography'],
    workExperience: [
      {
        jobTitle: 'Bank Teller',
        company: 'Double Star National Bank',
        startDate: '08/2017',
        endDate: 'Present',
        location: 'Harrisburg, PA',
        responsibilities: [
          'Administer the balancing of the branch’s vaults, Automated Teller Machines (ATMs), Teller Cash Dispensers (TCDs), and Teller Cash Recyclers (TCRs) with a daily total of more than $200K.',
          'Leverage organizational and prioritization abilities, coupled with superior attention to detail to efficiently process 20+ transactions per hour, attending to 50+ customers on a daily basis.',
          'Establish strong credibility with clients by providing accurate information to address concerns regarding their accounts and other bank products, resulting in achieving the highest rate of customer loyalty and retention.',
          'Serve as a Team Lead in the absence of the supervisor to maintain seamless bank operations and procedures.'
        ]
      },
      {
        jobTitle: 'Bank Teller/Lead',
        company: 'Bank of Pennsylvania, Lancaster Branch',
        startDate: '07/2013',
        endDate: '06/2017',
        location: 'Lancaster, PA',
        responsibilities: [
          'Demonstrated leadership in implementing adequate training to team members, resulting in improved performance and customer service delivery, which increased the existing client base by more than 30%.',
          'Provided efficient support to the Assistant Manager in overseeing the day-to-day operations to ensure an efficient and productive business workflow.',
          'Maximized customer satisfaction by catering a proactive day-to-day call center assistance to address client concerns.',
          'Contributed to designing and delivering effective training programs for newly hired employees to acquaint them with the bank’s policies and operational procedures.'
        ]
      },
      {
        jobTitle: 'Banking & Finance Intern',
        company: 'Blue Verticals Credit Union',
        startDate: '06/2012',
        endDate: '06/2013',
        location: 'Philadelphia, PA',
        responsibilities: [] // Add responsibilities if any
      }
    ],
    education: [
      {
        degree: 'MS. in Banking and Finance',
        institution: 'The University of Philadelphia',
        startDate: '2010',
        endDate: '2012'
      }
    ]
  };

  const {
    personalInfo,
    skills,
    languages,
    certificates,
    awards,
    interests,
    workExperience,
    education
  } = data;

  return (
    <div className={styles.resumeContainer}>
      <div className={styles.leftColumn}>
        <div className={styles.profilePicContainer}>
          <img src={personalInfo.profilePicture} alt={`${personalInfo.firstName} ${personalInfo.lastName}`} className={styles.profilePic} />
        </div>

        {skills && skills.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}><i className={`fas fa-cogs ${styles.iconDiamond}`}></i> SKILLS</h2>
            <ul className={styles.skillsList}>
              {skills.map((skill, index) => (
                <li key={index} className={styles.skillItem}>{skill}</li>
              ))}
            </ul>
          </section>
        )}

        {languages && languages.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}><i className={`fas fa-language ${styles.iconDiamond}`}></i> LANGUAGES</h2>
            {languages.map((lang, index) => (
              <div key={index} className={styles.languageItem}>
                <p className={styles.languageName}>{lang.language}</p>
                <p className={styles.languageProficiency}>{lang.proficiency}</p>
              </div>
            ))}
          </section>
        )}

        {certificates && certificates.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}><i className={`fas fa-certificate ${styles.iconDiamond}`}></i> CERTIFICATES</h2>
            {certificates.map((cert, index) => (
              <div key={index} className={styles.certificateItem}>
                <p className={styles.certificateName}>
                  {cert.name}
                  {cert.link && cert.link !== '#' && <a href={cert.link} target="_blank" rel="noopener noreferrer" className={styles.certLink}> <i className="fas fa-external-link-alt"></i></a>}
                </p>
                <p className={styles.certificateOrg}>{cert.issuingOrganization}</p>
              </div>
            ))}
          </section>
        )}

        {awards && awards.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}><i className={`fas fa-award ${styles.iconDiamond}`}></i> HONOR AWARDS</h2>
            {awards.map((award, index) => (
              <div key={index} className={styles.awardItem}>
                <p className={styles.awardName}>{award.name}</p>
                <p className={styles.awardInstitution}>{award.institution}</p>
              </div>
            ))}
          </section>
        )}

        {interests && interests.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}><i className={`fas fa-heart ${styles.iconDiamond}`}></i> INTERESTS</h2>
            <ul className={styles.interestsList}>
              {interests.map((interest, index) => (
                <li key={index} className={styles.interestItem}>
                  {/* Add appropriate icons based on interest text or use a generic one */}
                  {interest === 'Mediation' && <i className={`fas fa-users ${styles.interestIcon}`}></i>}
                  {interest === 'Archery' && <i className={`fas fa-bullseye ${styles.interestIcon}`}></i>}
                  {interest === 'Gardening' && <i className={`fas fa-seedling ${styles.interestIcon}`}></i>}
                  {interest === 'Photography' && <i className={`fas fa-camera ${styles.interestIcon}`}></i>}
                  {interest}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <div className={styles.rightColumn}>
        <header className={styles.header}>
          <h1 className={styles.name}>{`${personalInfo.firstName} ${personalInfo.lastName}`}</h1>
          <h2 className={styles.jobTitle}>{personalInfo.jobTitle}</h2>
          <p className={styles.summary}>{personalInfo.summary}</p>
        </header>

        <div className={styles.contactInfo}>
          {personalInfo.email && <p><i className={`fas fa-envelope ${styles.contactIcon}`}></i> {personalInfo.email}</p>}
          {personalInfo.phone && <p><i className={`fas fa-phone ${styles.contactIcon}`}></i> {personalInfo.phone}</p>}
          {personalInfo.address && <p><i className={`fas fa-map-marker-alt ${styles.contactIcon}`}></i> {personalInfo.address}</p>}
          {personalInfo.linkedin && <p><i className={`fab fa-linkedin ${styles.contactIcon}`}></i> {personalInfo.linkedin}</p>}
          {personalInfo.skype && <p><i className={`fab fa-skype ${styles.contactIcon}`}></i> {personalInfo.skype}</p>}
        </div>

        {workExperience && workExperience.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitleRight}><i className={`fas fa-briefcase ${styles.iconDiamondRight}`}></i> WORK EXPERIENCE</h2>
            {workExperience.map((job, index) => (
              <div key={index} className={styles.jobItem}>
                <div className={styles.jobHeader}>
                  <h3 className={styles.jobTitleCompany}>{job.jobTitle} | {job.company}</h3>
                  <span className={styles.jobDatesLocation}>{job.startDate} - {job.endDate} | {job.location}</span>
                </div>
                {renderListItems(job.responsibilities)}
              </div>
            ))}
          </section>
        )}

        {education && education.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitleRight}><i className={`fas fa-graduation-cap ${styles.iconDiamondRight}`}></i> EDUCATION</h2>
            {education.map((edu, index) => (
              <div key={index} className={styles.educationItem}>
                 <div className={styles.jobHeader}>
                    <h3 className={styles.degreeInstitution}>{edu.degree}</h3>
                    <span className={styles.jobDatesLocation}>{edu.startDate} - {edu.endDate}</span>
                 </div>
                <p className={styles.educationInstitution}>{edu.institution}</p>
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

export default ResumeTemplateElegant;